import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

export function CreateLeadModal({ open, onClose, onSuccess, editLead }) {
    const [formData, setFormData] = useState({
        firstName: editLead?.name?.split(" ")[0] || "",
        lastName: editLead?.name?.split(" ")?.slice(1).join(" ") || "",
        email: editLead?.email || "",
        phone: editLead?.phone || "",
        secondPhone: editLead?.secondPhone || "",
        status: editLead?.status || "New",
        desk: editLead?.desk || "",
        source: editLead?.source || "",
        country: editLead?.country || "",
        city: editLead?.city || "",
        address: editLead?.address || "",
        lastComment: editLead?.lastComment || "",
        extra: editLead?.extra || ""
    });

    const statuses = ["New", "Interested", "Follow-up", "Converted", "Not Answered"];
    const desks = ["Sales Desk A", "Sales Desk B", "Retention Desk", "VIP Desk"];
    const deskManagers = ["John Smith", "Sarah Johnson", "Mike Wilson", "Emily Davis"];
    const countries = ["USA", "UK", "Germany", "France", "Spain", "Italy", "Canada"];

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate that either firstName or lastName is provided
        const hasFirstName = formData.firstName && formData.firstName.trim() !== "";
        const hasLastName = formData.lastName && formData.lastName.trim() !== "";
        if (!hasFirstName && !hasLastName) {
            toast.error("First Name or Last Name is required");
            return;
        }

        if (!formData.email) {
            toast.error("Email is required");
            return;
        }

        // Generate a default password when creating a new lead (email + timestamp if email exists, otherwise random string)
        const defaultPassword = editLead 
            ? "" 
            : (formData.email 
                ? `${formData.email}_${Date.now()}` 
                : `lead_${Date.now()}_${Math.random().toString(36).substring(7)}`);

        const leadData = {
            id: editLead?.id || `LEAD-${Date.now()}`,
            name: `${formData.firstName} ${formData.lastName}`.trim(),
            email: formData.email,
            phone: formData.phone,
            secondPhone: formData.secondPhone,
            password: editLead ? "" : defaultPassword,
            status: formData.status,
            desk: formData.desk,
            source: formData.source,
            country: formData.country,
            city: formData.city,
            address: formData.address,
            extra: formData.extra,
            // When creating a new lead, copy extra field content to lastComment
            lastComment: editLead ? formData.lastComment : (formData.extra ? formData.extra : formData.lastComment || "")
        };

        onSuccess(leadData);
        toast.success(editLead ? "Lead updated successfully!" : "Lead created successfully!");
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{editLead ? "Edit Lead" : "Create New Lead"}</DialogTitle>
                    <DialogDescription>
                        {editLead
                            ? "Update the lead information below."
                            : "Fill in the information below to create a new lead."}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Information */}
                    <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                            required
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="firstName">First Name <span className="text-gray-500 text-sm">(or Last Name required)</span></Label>
                            <Input
                                id="firstName"
                                value={formData.firstName}
                                onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label htmlFor="lastName">Last Name <span className="text-gray-500 text-sm">(or First Name required)</span></Label>
                            <Input
                                id="lastName"
                                value={formData.lastName}
                                onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="phone">Phone Number</Label>
                            <Input
                                id="phone"
                                value={formData.phone}
                                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label htmlFor="secondPhone">Second Phone</Label>
                            <Input
                                id="secondPhone"
                                value={formData.secondPhone}
                                onChange={(e) => setFormData((prev) => ({ ...prev, secondPhone: e.target.value }))}
                            />
                        </div>
                    </div>

                    {/* Location Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="country">Country</Label>
                            <Select
                                options={countries.map((c) => ({ label: c, value: c }))}
                                value={formData.country}
                                onChange={(value) => setFormData((prev) => ({ ...prev, country: value }))}
                                placeholder="Select country"
                                searchable
                            />
                        </div>
                        <div>
                            <Label htmlFor="city">City</Label>
                            <Input
                                id="city"
                                value={formData.city}
                                onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label htmlFor="address">Address</Label>
                            <Input
                                id="address"
                                value={formData.address}
                                onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label htmlFor="status">Status</Label>
                            <Select
                                options={statuses.map((s) => ({ label: s, value: s }))}
                                value={formData.status}
                                onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
                                placeholder="Select status"
                                searchable
                            />
                        </div>
                    </div>

                    {/* Assignment Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="deskManager">Manager</Label>
                            <Select
                                options={deskManagers.map((m) => ({ label: m, value: m }))}
                                value={formData.deskManager}
                                onChange={(value) => setFormData((prev) => ({ ...prev, deskManager: value }))}
                                placeholder="Select manager"
                                searchable
                            />
                        </div>
                        <div>
                            <Label htmlFor="desk">Desk</Label>
                            <Select
                                options={desks.map((d) => ({ label: d, value: d }))}
                                value={formData.desk}
                                onChange={(value) => setFormData((prev) => ({ ...prev, desk: value }))}
                                placeholder="Select desk"
                                searchable
                            />
                        </div>
                    </div>

                    {/* Source Information */}
                    <div>
                        <Label htmlFor="source">Source</Label>
                        <Input
                            id="source"
                            type="text"
                            value={formData.source}
                            onChange={(e) => setFormData((prev) => ({ ...prev, source: e.target.value }))}
                        />
                    </div>

                    <div>
                        <Label htmlFor="extra">Extra Notes</Label>
                        <Textarea
                            id="extra"
                            value={formData.extra}
                            onChange={(e) => setFormData((prev) => ({ ...prev, extra: e.target.value }))}
                            rows={2}
                        />
                    </div>

                    <div className="flex space-x-4 pt-4">
                        <Button type="submit" className="">
                            {editLead ? "Update Lead" : "Create Lead"}
                        </Button>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
